'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

// Every row on the site that scrolls sideways - listing rows, articles,
// agencies, a team, the chips of applied filters - fades out at whichever end
// still has something beyond it. One component, so the fade looks the same
// everywhere (owner's request, 2026-09-23: "as in the blog, the same all over
// the site"): black straight into transparent, 24 px wide on a phone and
// 48 px from 640 px up - about a row's own side padding, so at rest it lies on
// empty space and, scrolled, softens only a card's edge without darkening what
// is on it (owner, 2026-09-24; it was 48/80 with a half-black middle, which
// weighed on the cards). Without a fade the last card is simply cut
// by the screen and nothing says the row can be moved.
//
// The fades cover the edges of the wrapper, so the wrapper must be as wide as
// the visible row: a row that bleeds to the screen edge (-mx-*) bleeds with
// its wrapper, not inside it, or the fade lands in the middle of a card.

export default function ScrollRow({
  children,
  className = '',
  id,
  rowRef,
  fade = 'from-black',
  width = 'w-6 sm:w-12',
  wrapperClassName = 'relative min-w-0 flex-1',
}: {
  children: React.ReactNode;
  /** Classes for the scrolling row itself. */
  className?: string;
  /** The scrolling row's id (for arrows that scroll it). */
  id?: string;
  /** The scrolling row's element, for a parent that scrolls it itself. */
  rowRef?: React.RefObject<HTMLDivElement | null>;
  /** Gradient colours, matching what is behind the row. */
  fade?: string;
  /** How wide each fade is. */
  width?: string;
  wrapperClassName?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const setRow = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el;
      if (rowRef) rowRef.current = el;
    },
    [rowRef]
  );

  useEffect(() => {
    const row = ref.current;
    if (!row) return;
    const update = () =>
      setEdges({
        left: row.scrollLeft > 2,
        right: row.scrollLeft + row.clientWidth < row.scrollWidth - 2,
      });
    const frame = requestAnimationFrame(update);
    // The web fonts land after the first measurement and make the row wider
    // without changing its own box, so a ResizeObserver on it never fires:
    // measure again when they are ready, and once more shortly after in case
    // something else (an image, a late count) still has to settle.
    const later = setTimeout(update, 400);
    document.fonts?.ready.then(update).catch(() => undefined);
    row.addEventListener('scroll', update, { passive: true });
    const resize = new ResizeObserver(update);
    resize.observe(row);
    for (const child of Array.from(row.children)) resize.observe(child);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(later);
      row.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      resize.disconnect();
    };
  }, []);

  return (
    <div className={wrapperClassName}>
      <div ref={setRow} id={id} className={className}>
        {children}
      </div>
      {edges.left && (
        <span aria-hidden="true" className={`pointer-events-none absolute inset-y-0 left-0 z-10 ${width} bg-gradient-to-r ${fade} to-transparent`} />
      )}
      {edges.right && (
        <span aria-hidden="true" className={`pointer-events-none absolute inset-y-0 right-0 z-10 ${width} bg-gradient-to-l ${fade} to-transparent`} />
      )}
    </div>
  );
}
